import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { bindingLabel, emptyEntityFilters } from '../utilities/adminSearch';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import {
  bindingsFromFilters,
  extractNewsTags,
  isValidNewsTagKey,
  normalizeNewsTagKey,
  parseNewsTag,
  parseNewsTags,
} from '../utilities/newsApi';
import NewsEntityFilters from './NewsEntityFilters';

export default function NewsTagPicker({
  value = [],
  onChange,
  disabled,
  allowCreate = true,
  withBindings = false,
}) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const selected = parseNewsTags(value);
  const selectedKeys = selected.map((tag) => tag.key);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draftBindings, setDraftBindings] = useState(emptyEntityFilters);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open || disabled) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      loadTags(query);
    }, query ? 250 : 0);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, open, disabled]);

  const loadTags = async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const body = {};
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminNewsTags, body);
      if (requestId !== requestIdRef.current) return;
      setSuggestions(extractNewsTags(response.data).map(parseNewsTag).filter(Boolean));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setError(getApiErrorMessage(err, t('newsTagsLoadError')));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const addTag = (raw) => {
    const parsed = parseNewsTag(raw);
    const key = parsed?.key || normalizeNewsTagKey(raw);
    if (!key || selectedKeys.includes(key)) {
      setQuery('');
      return;
    }
    if (!isValidNewsTagKey(key)) {
      setError(t('newsTagInvalid'));
      return;
    }
    const fromSuggestion = parsed && typeof raw === 'object';
    const nextTag = {
      key,
      bindings: fromSuggestion
        ? parsed.bindings
        : withBindings
          ? bindingsFromFilters(draftBindings)
          : parsed?.bindings || bindingsFromFilters(emptyEntityFilters()),
    };
    onChange(selected.concat(nextTag));
    setQuery('');
    setError('');
    setOpen(false);
  };

  const removeTag = (key) => {
    onChange(selected.filter((item) => item.key !== key));
  };

  const draft = normalizeNewsTagKey(query);
  const visible = suggestions.filter((item) => item?.key && !selectedKeys.includes(item.key));
  const canCreate = allowCreate
    && draft
    && isValidNewsTagKey(draft)
    && !selectedKeys.includes(draft)
    && !visible.some((item) => item.key === draft);

  return (
    <div className={`news-tags ${disabled ? 'is-disabled' : ''}`} ref={wrapRef}>
      {withBindings ? (
        <NewsEntityFilters
          value={draftBindings}
          onChange={setDraftBindings}
          disabled={disabled}
          hint={t('newsTagBindingsHint')}
        />
      ) : null}

      <div className="news-tag-search-wrap">
        <div className="st-search news-tag-search">
          <FiSearch className="st-search-icon" />
          <input
            type="text"
            className="st-search-input"
            placeholder={allowCreate ? t('newsTagsPh') : t('newsTagsFilterPh')}
            value={query}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setError('');
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (canCreate || (draft && selectedKeys.includes(draft))) addTag(draft);
                else if (visible[0]) addTag(visible[0]);
              }
              if (event.key === 'Escape') setOpen(false);
            }}
          />
          {query ? (
            <button
              type="button"
              className="st-search-clear"
              onClick={() => setQuery('')}
            >
              ×
            </button>
          ) : null}
        </div>

        {open && !disabled ? (
          <div className="news-tag-dropdown">
            {loading ? <div className="news-tag-empty">{t('newsTagsLoading')}</div> : null}
            {!loading && visible.map((item) => {
              const extra = bindingLabel(item, t);
              return (
                <button
                  key={item.key}
                  type="button"
                  className="news-tag-option"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => addTag(item)}
                >
                  <span>{item.key}</span>
                  <small>{extra || (item.news_count != null ? item.news_count : '')}</small>
                </button>
              );
            })}
            {canCreate ? (
              <button
                type="button"
                className="news-tag-option is-create"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => addTag(draft)}
              >
                <FiPlus /> {t('newsTagAdd', { key: draft })}
              </button>
            ) : null}
            {!loading && !visible.length && !canCreate ? (
              <div className="news-tag-empty">{t('newsTagsEmpty')}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {selected.length ? (
        <div className="news-tag-chips">
          {selected.map((tag) => {
            const extra = bindingLabel(tag, t);
            return (
              <span key={tag.key} className="usr-chip news-tag-chip">
                <span className="news-tag-chip-text">
                  {tag.key}
                  {extra ? <small>{extra}</small> : null}
                </span>
                {!disabled ? (
                  <button
                    type="button"
                    className="news-tag-remove"
                    onClick={() => removeTag(tag.key)}
                    aria-label={t('newsTagRemove', { key: tag.key })}
                  >
                    <FiX />
                  </button>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}

      {error ? <span className="st-field-error">{error}</span> : null}
    </div>
  );
}
